import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import { formatPhoneNumber } from '../utils/phone';
import './ClientLogin.css';

function ClientLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Check for redirect message
  const redirectMessage = location.state?.message;
  
  // Pre-filled when a guest booking is sent here because the email already has
  // an account (Book.js, account_exists). Plain email only; never a password.
  const [loginData, setLoginData] = useState({
    email: typeof location.state?.email === 'string' ? location.state.email : '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: '',
    enable2FA: false
  });

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

  // Detect browser autofill and sync to React state
  useEffect(() => {
    const timer = setTimeout(() => {
      const emailEl = document.querySelector('input[name="email"]');
      const passEl = document.querySelector('input[name="password"]');
      if (emailEl && emailEl.value && emailEl.value !== loginData.email) {
        setLoginData(prev => ({ ...prev, email: emailEl.value }));
      }
      if (passEl && passEl.value && passEl.value !== loginData.password) {
        setLoginData(prev => ({ ...prev, password: passEl.value }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get reCAPTCHA token (optional - proceed without if not available)
      let recaptchaToken = '';
      try {
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha('login');
        }
      } catch (recaptchaErr) {
        console.warn('reCAPTCHA failed, proceeding without:', recaptchaErr);
      }
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const trustedDevice = localStorage.getItem('trustedDeviceToken');
      const response = await axios.post(`${API_URL}/auth/login`, {
        ...loginData,
        recaptchaToken
      }, {
        headers: trustedDevice ? { 'X-Trusted-Device': trustedDevice } : {}
      });
      
      if (response.data.requires2FA) {
        // 2FA challenge — show code entry screen
        setTempToken(response.data.tempToken);
        setRequires2FA(true);
        setSuccess(response.data.message || 'Verification code sent to your email');
        setLoading(false);
        return;
      }

      // Normal login — store token
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      const backendRedirect = response.data.redirectTo;
      const localRedirect = localStorage.getItem('redirectAfterLogin');
      
      if (localRedirect) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(localRedirect);
      } else if (backendRedirect) {
        navigate(backendRedirect);
      } else {
        navigate('/client/portal');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please try again.';
      
      // Check if error is about unverified email
      if (errorMessage.toLowerCase().includes('verify') || errorMessage.toLowerCase().includes('verification')) {
        setError(errorMessage + ' ');
        setRegisteredEmail(loginData.email);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    try {
      const response = await axios.post(
        `${API_URL}/auth/verify-2fa`,
        { code: otpCode, trustDevice },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Store trusted device token if 2FA was used with trust checkbox
      if (response.data.trustedDeviceToken) {
        localStorage.setItem('trustedDeviceToken', response.data.trustedDeviceToken);
      }
      const localRedirect = localStorage.getItem('redirectAfterLogin');
      if (localRedirect) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(localRedirect);
      } else {
        navigate(response.data.redirectTo || '/client/portal');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    if (resendCooldown > 0) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    try {
      await axios.post(`${API_URL}/auth/resend-2fa`, {}, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      setSuccess('New code sent to your email');
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(c => {
          if (c <= 1) { clearInterval(interval); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not resend code');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (registerData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(registerData.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      setLoading(false);
      return;
    }

    try {
      // Get reCAPTCHA token (optional - proceed without if not available)
      let recaptchaToken = '';
      try {
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha('register');
        }
      } catch (recaptchaErr) {
        console.warn('reCAPTCHA failed, proceeding without:', recaptchaErr);
      }
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const response = await axios.post(`${API_URL}/auth/register`, {
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone,
        company: registerData.company,
        password: registerData.password,
        enable2FA: registerData.enable2FA,
        recaptchaToken
      });

      // Auto-login after registration
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Redirect — honor redirectAfterLogin (set by /book and other flows that
        // saved state before sending the user to authenticate). Falls back to
        // legacy returnPath, then to client portal.
        const localRedirect =
          localStorage.getItem('redirectAfterLogin') ||
          localStorage.getItem('returnPath');
        if (localRedirect) {
          localStorage.removeItem('redirectAfterLogin');
          localStorage.removeItem('returnPath');
          window.location.href = localRedirect;
        } else {
          window.location.href = '/client/portal';
        }
      } else {
        setSuccess('Account created successfully! Please log in.');
        setIsLogin(true);
      }
      
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(`${API_URL}/auth/resend-verification`, {
        email: registeredEmail
      });
      
      setSuccess('Verification email sent! Please check your inbox.');
    } catch (err) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotPasswordEmail });
      setSuccess('Password reset instructions have been sent to your email.');
      setForgotPasswordEmail('');
      
      setTimeout(() => {
        setShowForgotPassword(false);
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError('Error sending reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification prompt after registration
  if (showVerificationPrompt) {
    return (
      <div className="client-login-page">
        <div className="login-container">
          <div className="login-card" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className="login-header">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
              <h1>Verify Your Email</h1>
              <p>We've sent a verification email to:</p>
              <p style={{ fontWeight: 'bold', color: 'var(--link)', margin: '0.5rem 0 1rem' }}>
                {registeredEmail}
              </p>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <div style={{ 
              background: 'var(--info-bg)', 
              border: '1px solid var(--info-border)', 
              borderRadius: '8px', 
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--link-deep)' }}>
                Next Steps:
              </h3>
              <ol style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--link-deep)' }}>
                <li style={{ marginBottom: '0.5rem' }}>Check your email inbox</li>
                <li style={{ marginBottom: '0.5rem' }}>Click the verification link in the email</li>
                <li style={{ marginBottom: '0.5rem' }}>Return here to login</li>
              </ol>
              <p style={{ 
                margin: '1rem 0 0 0', 
                fontSize: '0.875rem', 
                color: 'var(--link-deep)',
                fontStyle: 'italic'
              }}>
                💡 Check your spam folder if you don't see the email within a few minutes.
              </p>
            </div>

            <button 
              type="button"
              onClick={handleResendVerification}
              className="btn btn-primary btn-block"
              disabled={loading}
              style={{ marginBottom: '1rem' }}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </button>

            <div className="form-footer">
              <button 
                type="button"
                onClick={() => {
                  setShowVerificationPrompt(false);
                  setIsLogin(true);
                }}
                className="link-button"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="client-login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1>Reset Password</h1>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
            </div>

            <form onSubmit={handleForgotPassword}>
              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <div className="form-footer">
                <button 
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="link-button"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── 2FA code entry screen ─────────────────────────────────────────────────
  if (requires2FA) {
    return (
      <div className="client-login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1>Check Your Email</h1>
              <p>Enter the 6-digit verification code we sent to your email</p>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <form onSubmit={handleVerify2FA} className="login-form">
              <div className="form-group">
                <label htmlFor="otpCode">Verification Code</label>
                <input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/D/g, ''))}
                  className="otp-input"
                  autoFocus
                />
              </div>

              <div className="trust-device-row">
                <label className="trust-device-label">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                  />
                  <span>Trust this browser for 30 days</span>
                </label>
              </div>

              <button type="submit" className="btn-primary" disabled={loading || otpCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>
            </form>

            <div className="two-fa-footer">
              <button
                className="resend-btn"
                onClick={handleResend2FA}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
              </button>
              <button
                className="back-btn"
                onClick={() => { setRequires2FA(false); setOtpCode(''); setError(''); setSuccess(''); }}
              >
                ← Back to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>{isLogin ? 'Client Portal' : 'Create Account'}</h1>
            <p>{isLogin ? 'Access your galleries and invoices' : 'Get access to your photo galleries'}</p>
          </div>

          <div className="login-tabs">
            <button
              className={`tab ${isLogin ? 'active' : ''}`}
              onClick={() => setIsLogin(true)}
            >
              Login
            </button>
            <button
              className={`tab ${!isLogin ? 'active' : ''}`}
              onClick={() => setIsLogin(false)}
            >
              Register
            </button>
          </div>

          {isLogin ? (
            <form onSubmit={handleLoginSubmit}>
              {redirectMessage && (
                <div className="alert" style={{ background: 'var(--sky-bg)', border: '1px solid var(--sky-br)', color: 'var(--sky-fg)', marginBottom: '1rem' }}>
                  {redirectMessage}
                </div>
              )}
              {error && (
                <div className="alert alert-error">
                  {error}
                  {(error.toLowerCase().includes('verify') || error.toLowerCase().includes('verification')) && registeredEmail && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        className="link-button"
                        style={{ color: 'var(--danger-hover)', textDecoration: 'underline' }}
                      >
                        Resend verification email
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  onInput={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  onInput={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  required
                />
              </div>

              <div className="form-options">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>

              <p style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                textAlign: 'center', 
                marginTop: '1rem',
                marginBottom: '0.5rem'
              }}>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--link)' }}>Privacy Policy</a> and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--link)' }}>Terms of Service</a> apply.
              </p>

              <div className="form-footer">
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className="link-button"
                  >
                    Register here
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegisterSubmit}>
              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: formatPhoneNumber(e.target.value) })}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Company (Optional)</label>
                  <input
                    type="text"
                    value={registerData.company}
                    onChange={(e) => setRegisterData({ ...registerData, company: e.target.value })}
                    placeholder="Your Company"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="At least 8 characters"
                  required
                />
                <small className="form-text">Minimum 8 characters</small>
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  placeholder="Re-enter password"
                  required
                />
              </div>

              {/* Two-Factor Authentication Option */}
              <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--info-bg)', borderRadius: '8px', border: '1px solid var(--info-border)' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={registerData.enable2FA}
                    onChange={(e) => setRegisterData({ ...registerData, enable2FA: e.target.checked })}
                    style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div>
                    <strong style={{ color: 'var(--link-deep)' }}>Enable Two-Factor Authentication (Recommended)</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--link-deep)' }}>
                      Add an extra layer of security to your account. You'll receive a verification code via text message when logging in.
                    </p>
                  </div>
                </label>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <p style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                textAlign: 'center', 
                marginTop: '1rem',
                marginBottom: '0.5rem'
              }}>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--link)' }}>Privacy Policy</a> and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--link)' }}>Terms of Service</a> apply.
              </p>

              <div className="form-footer">
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setIsLogin(true)}
                    className="link-button"
                  >
                    Login here
                  </button>
                </p>
              </div>
            </form>
          )}

          <div className="login-help">
            <p>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
              Need help? <Link to="/contact">Contact us</Link>
            </p>
          </div>
        </div>

        <div className="login-benefits">
          <h3>Client Portal Benefits</h3>
          <ul>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Access all your photo galleries</span>
            </li>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Download photos anytime</span>
            </li>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>View and pay invoices</span>
            </li>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Book new sessions quickly</span>
            </li>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Track your appointment history</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ClientLogin;
